import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ToastComponent } from '@tylertech/forge';
import { Context } from '../store/Store.js';
import { REDUCER_ACTION_TYPES } from '../store/Reducer.js';
import { filterByActiveCostCenters, filterByUserAssignedCostCenters } from '../services/programService.js';
import { saveScores } from '../services/resultsService.js';
import { useUser } from './userContext.js';
import { useNameConventions } from './nameConventionsContext.js';
import { LoaderContext } from './loaderContext.js';
import { BPA, COMMUNITY, GOVERNANCE } from '../static/keyWords.js';
import { filterByDepartment, filterByDivision, mapScoresForSaving } from '../components/DepartmentScoring/departmentScoringHelpers.js';
import { FiltersContext } from '../components/reusable/Filters/filterContext.js';
import { FILTER_TYPES } from '../components/reusable/Filters/filterHelpers.js';
import { messageManager } from '../components/System/messageManager/messageManager.js';
import useDepartmentScoringData from '../customHooks/useDepartmentScoringData.js';

const DepartmentScoringContext = createContext(null);
const DepartmentScoringEditingContext = createContext(null);

export const DepartmentScoringProvider = ({ children }) => {
  const [activeStep, setActiveStep] = useState(null);
  const [cardSelected, setCardSelected] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [newScores, setNewScores] = useState({});

  const [state, dispatch] = useContext(Context);
  const { appliedFilters, resetFilters } = useContext(FiltersContext);
  const { user } = useUser();
  const { nameConventions } = useNameConventions();
  const { setLoading } = useContext(LoaderContext);
  const { programs, results, resultTypes, loadScoringData } = useDepartmentScoringData();

  const cardSelectedRef = useRef(cardSelected);
  const newScoresRef = useRef(newScores);

  cardSelectedRef.current = cardSelected;
  newScoresRef.current = newScores;

  const filteredPrograms = useMemo(() => {
    const activeCostCenterPrograms = filterByActiveCostCenters(programs, user);
    const assignedCostCenterPrograms = filterByUserAssignedCostCenters(activeCostCenterPrograms, user);
    const departmentFilter = appliedFilters.find(({ type }) => type === FILTER_TYPES.DEPARTMENT);
    const divisionFilter = appliedFilters.find(({ type }) => type === FILTER_TYPES.DIVISION);
    const departmentPrograms = filterByDepartment(assignedCostCenterPrograms, departmentFilter);

    return filterByDivision(departmentPrograms, divisionFilter);
  }, [appliedFilters, programs, user]);

  const programsForResultTypeStep = useMemo(() => ({
    [BPA]: filteredPrograms.filter((program) => program.serviceTypeId === 4 || program.serviceTypeId === 6),
    [COMMUNITY]: filteredPrograms.filter((program) => program.serviceTypeId === 4),
    [GOVERNANCE]: filteredPrograms.filter((program) => program.serviceTypeId === 6),
  }), [filteredPrograms]);

  const updateNewScores = useCallback((scores) => {
    setNewScores(scores);
    dispatch({ type: REDUCER_ACTION_TYPES.SET_NEW_SCORES, payload: scores });
  }, [dispatch]);

  const resetNewScores = useCallback(() => {
    setNewScores({});
    dispatch({ type: REDUCER_ACTION_TYPES.SET_NEW_SCORES, payload: {} });
  }, [dispatch]);

  const handleTabChange = useCallback((stepIndex) => {
    if (state.fireUnsavedChangesDialog) {
      dispatch({ type: REDUCER_ACTION_TYPES.SET_USER_NAVIGATED, payload: !state.userNavigated });
      return;
    }
    setActiveStep(stepIndex);
  }, [dispatch, state.fireUnsavedChangesDialog, state.userNavigated]);

  const handleUnsavedChanges = useCallback((value) => {
    dispatch({ type: REDUCER_ACTION_TYPES.FIRE_UNSAVED_CHANGES_DIALOG, payload: value });
  }, [dispatch]);

  const handleSave = useCallback(async () => {
    const selectedResult = cardSelectedRef.current;
    const scoresForResult = selectedResult ? newScoresRef.current[selectedResult.name] : null;

    if (!selectedResult || !scoresForResult || Object.keys(scoresForResult).length === 0) {
      dispatch({ type: REDUCER_ACTION_TYPES.SAVE_UNSAVED_CHANGES, payload: false });
      dispatch({
        type: REDUCER_ACTION_TYPES.FIRE_UNSAVED_CHANGES_DIALOG,
        payload: Boolean(scoresForResult),
      });
      return false;
    }

    const scoresToSave = [];
    mapScoresForSaving(scoresToSave, newScoresRef.current, selectedResult.name, selectedResult.id);

    setLoading(true);
    try {
      await saveScores(scoresToSave, { disallowGlobalBadRequestPopups: true });
      await loadScoringData();
      resetNewScores();
      setCardSelected(null);

      if (state.backButtonFired) {
        setActiveStep(null);
        dispatch({ type: REDUCER_ACTION_TYPES.BACK_BUTTON_FIRED, payload: false });
      }

      dispatch({ type: REDUCER_ACTION_TYPES.FIRE_UNSAVED_CHANGES_DIALOG, payload: false });
      dispatch({ type: REDUCER_ACTION_TYPES.SET_USER_NAVIGATED, payload: !state.userNavigated });
      dispatch({ type: REDUCER_ACTION_TYPES.SAVE_UNSAVED_CHANGES, payload: false });
      ToastComponent.present({
        message: messageManager(nameConventions).DEPARTMENT_SCORING.STEP_SAVING_SUCCESS,
      });
      return true;
    } catch {
      dispatch({ type: REDUCER_ACTION_TYPES.SAVE_UNSAVED_CHANGES, payload: false });
      dispatch({ type: REDUCER_ACTION_TYPES.FIRE_UNSAVED_CHANGES_DIALOG, payload: true });
      ToastComponent.present({
        message: messageManager(nameConventions).DEPARTMENT_SCORING.STEP_SAVING_ERROR,
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [dispatch, loadScoringData, nameConventions, resetNewScores, setLoading, state.backButtonFired, state.userNavigated]);

  const handleCancel = useCallback((backButton = false) => {
    setCardSelected(null);
    resetNewScores();

    if (state.backButtonFired || backButton) {
      setActiveStep(null);
      dispatch({ type: REDUCER_ACTION_TYPES.BACK_BUTTON_FIRED, payload: false });
    }

    dispatch({ type: REDUCER_ACTION_TYPES.FIRE_UNSAVED_CHANGES_DIALOG, payload: false });
    dispatch({ type: REDUCER_ACTION_TYPES.SET_USER_NAVIGATED, payload: !state.userNavigated });
    dispatch({ type: REDUCER_ACTION_TYPES.DISCARD_UNSAVED_CHANGES, payload: false });
  }, [dispatch, resetNewScores, state.backButtonFired, state.userNavigated]);

  useEffect(() => {
    resetFilters();
    setActiveStep(null);
    setCardSelected(null);
    setIsDrawerOpen(false);
    resetNewScores();
    loadScoringData();
  }, [user?.activeBudget,user?.activeCostCenters,user?.activeOrganization ]);

  useEffect(() => {
    if (state.saveUnsavedChanges) {
      handleSave();
    } else if (state.discardUnsavedChanges) {
      handleCancel();
    }
  }, [handleCancel, handleSave, state.discardUnsavedChanges, state.saveUnsavedChanges]);

  const value = useMemo(() => ({
    activeStep,
    cardSelected,
    isDrawerOpen,
    setCardSelected,
    setIsDrawerOpen,
    programsArr: programs,
    resultsArr: results,
    resultTypes,
    programsForResultTypeStep,
    handleTabChange,
    handleCancel,
    handleSave,
    prepare: loadScoringData,
  }), [
    activeStep,
    cardSelected,
    isDrawerOpen,
    handleCancel,
    handleSave,
    handleTabChange,
    loadScoringData,
    programs,
    programsForResultTypeStep,
    results,
    resultTypes,
    setCardSelected,
    setIsDrawerOpen,
  ]);

  const editingValue = useMemo(() => ({
    newScores,
    updateNewScores,
    handleUnsavedChanges,
  }), [handleUnsavedChanges, newScores, updateNewScores]);

  return (
    <DepartmentScoringContext.Provider value={value}>
      <DepartmentScoringEditingContext.Provider value={editingValue}>
        {children}
      </DepartmentScoringEditingContext.Provider>
    </DepartmentScoringContext.Provider>
  );
};

DepartmentScoringProvider.propTypes = {
  children: PropTypes.node,
};

export const useDepartmentScoring = () => {
  const context = useContext(DepartmentScoringContext);
  if (!context) {
    throw new Error('useDepartmentScoring must be used within a DepartmentScoringProvider');
  }
  return context;
};

export const useDepartmentScoringEditing = () => {
  const context = useContext(DepartmentScoringEditingContext);
  if (!context) {
    throw new Error('useDepartmentScoringEditing must be used within a DepartmentScoringProvider');
  }
  return context;
};
